import dayjs from 'dayjs';
import type { PageData } from 'types/Page';
import type { HealthStatus, PageHealth } from 'api/new-analytics/types';
import type { AnalyticsSyncButtonState } from 'app/modules/analytics/types/AnalyticsSyncButtonState';

const SYNC_RECOMMENDED_STATUSES = new Set<HealthStatus>([
  'unknown',
  'stale',
  'degraded',
]);

function rowNeedsSyncRecommendation(row: PageHealth | undefined): boolean {
  return row != null && SYNC_RECOMMENDED_STATUSES.has(row.health_status);
}

export function isPageHealthTrackingOff(row: PageHealth | undefined): boolean {
  return (
    row != null &&
    (row.health_status === 'disabled' || row.status === 'disabled')
  );
}

export function getAnalyticsContentHeaderTrackingDisabledAt(
  isGroupSelected: boolean,
  enabledGroupPageIds: ReadonlySet<number>,
  getPageHealth: (pageId: number) => PageHealth | undefined,
  selectedPageHealth: PageHealth | undefined,
): string | null {
  if (isGroupSelected) {
    const pageIds = Array.from(enabledGroupPageIds);
    if (
      pageIds.length === 0 ||
      !pageIds.every((pageId) => isPageHealthTrackingOff(getPageHealth(pageId)))
    ) {
      return null;
    }
    const dates = pageIds
      .map((pageId) => getPageHealth(pageId)?.disabled_at)
      .filter((d): d is string => d != null);
    if (dates.length === 0) return null;
    return dates.reduce((earliest, d) =>
      dayjs(d).isBefore(dayjs(earliest)) ? d : earliest,
    );
  }
  if (!isPageHealthTrackingOff(selectedPageHealth)) return null;
  return selectedPageHealth?.disabled_at ?? null;
}

export function getAnalyticsContentHeaderShowReconnectMode(
  isGroupSelected: boolean,
  selectedPage: PageData | null,
  selectedPageHealth: PageHealth | undefined,
): boolean {
  if (isGroupSelected || selectedPage == null || !selectedPage.type) {
    return false;
  }
  return selectedPageHealth?.health_status === 'token_expired';
}

export function getAnalyticsContentHeaderIsSyncRecommended(
  showReconnectMode: boolean,
  isGroupSelected: boolean,
  enabledGroupPageIds: ReadonlySet<number>,
  getPageHealth: (pageId: number) => PageHealth | undefined,
  selectedPageHealth: PageHealth | undefined,
): boolean {
  if (showReconnectMode) {
    return false;
  }
  if (isGroupSelected) {
    return Array.from(enabledGroupPageIds).some((pageId) =>
      rowNeedsSyncRecommendation(getPageHealth(pageId)),
    );
  }
  return rowNeedsSyncRecommendation(selectedPageHealth);
}

export function getAnalyticsContentHeaderSyncButtonState(params: {
  isShowingSuccess: boolean;
  isSyncing: boolean;
  syncTargetMatchesSelection: boolean;
  showReconnectMode: boolean;
  isHealthLoading: boolean;
  selectedPage: PageData | null;
  isGroupSelected: boolean;
  enabledGroupPageIds: ReadonlySet<number>;
  getPageHealth: (pageId: number) => PageHealth | undefined;
  selectedPageHealth: PageHealth | undefined;
}): AnalyticsSyncButtonState {
  const {
    isShowingSuccess,
    isSyncing,
    syncTargetMatchesSelection,
    showReconnectMode,
    isHealthLoading,
    selectedPage,
    isGroupSelected,
    enabledGroupPageIds,
    getPageHealth,
    selectedPageHealth,
  } = params;
  const successForSelection = isShowingSuccess && syncTargetMatchesSelection;
  const syncingForSelection = isSyncing && syncTargetMatchesSelection;
  if (successForSelection) return 'success';
  if (syncingForSelection) return 'syncing';
  if (showReconnectMode) return 'reconnect';

  if (isHealthLoading) {
    return 'idle';
  }
  if (!isGroupSelected && selectedPage == null) {
    return 'idle';
  }

  if (isGroupSelected) {
    const pageIds = Array.from(enabledGroupPageIds);
    if (
      pageIds.length > 0 &&
      pageIds.every((pageId) => isPageHealthTrackingOff(getPageHealth(pageId)))
    ) {
      return 'sync-paused';
    }
    const healthList = pageIds.map(getPageHealth);
    if (
      healthList.some((pageHealth) => pageHealth?.health_status === 'degraded')
    ) {
      return 'error';
    }
    if (
      healthList.length > 0 &&
      healthList.every((pageHealth) => !pageHealth?.last_successful_sync_at)
    ) {
      return 'first-sync';
    }
    return 'idle';
  }

  if (isPageHealthTrackingOff(selectedPageHealth)) {
    return 'sync-paused';
  }
  if (selectedPageHealth?.health_status === 'degraded') return 'error';
  if (
    !selectedPageHealth?.last_successful_sync_at ||
    selectedPageHealth.health_status === 'unknown'
  ) {
    return 'first-sync';
  }
  return 'idle';
}

export function getAnalyticsContentHeaderSyncTestIds(
  syncButtonState: AnalyticsSyncButtonState,
  isSyncRecommended: boolean,
): { dataCy: string; dataName: string } {
  if (syncButtonState === 'sync-paused') {
    return {
      dataCy: 'analytics_content-header_sync-paused',
      dataName: 'analytics_content-header_sync-paused',
    };
  }
  if (syncButtonState === 'reconnect') {
    return {
      dataCy: 'analytics_content-header_reconnect-button',
      dataName: 'analytics_content-header_reconnect-button',
    };
  }
  if (syncButtonState === 'error' || isSyncRecommended) {
    return {
      dataCy: 'analytics_content-header_sync-button-recommended',
      dataName: 'analytics_content-header_sync-button-recommended',
    };
  }
  return {
    dataCy: 'analytics_content-header_sync-button',
    dataName: 'analytics_content-header_sync-button',
  };
}
